import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../../../../../utils/functions';
import { getLocalCajaManagementByLocalId } from '../../../../../../../../../../../../crud/management_api/locales_caja_management';
import './CashRefund.scss';
import { FaTriangleExclamation, FaMoneyBillTransfer } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

interface CashRefundProps {
  amount: number;
  show: boolean;
  setPayload: (payload: any) => void;
  setIsDisabled: any;
}

const CashRefund: React.FC<CashRefundProps> = ({
  amount,
  show,
  setPayload,
  setIsDisabled
}) => {
  const user = JSON.parse(localStorage.getItem('user') ?? '{}');
  const localId = Number(user.local_id) || 0;
  const userId = Number(user.user_id) || 0;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [workstations, setWorkstations] = useState<{
    current_amount: number;
    workstation_id: number;
    workstation_name: string;
  }[]>([]);
  const [selectedWorkstation, setSelectedWorkstation] = useState<number | null>(null);
  const [storedAmount, setStoredAmount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null)

  // 1) Fetch open workstations whenever the modal is shown
  useEffect(() => {
    if (!show) {
      // Reset state when modal closes
      setIsLoading(true);
      setWorkstations([]);
      setSelectedWorkstation(null);
      setStoredAmount(0);
      setError(null);
      return;
    }
    setIsLoading(true);
    getLocalCajaManagementByLocalId(localId)
      .then((response: any) => {
        const open_ws = response.data
          .filter((ws: any) => ws.is_open)
          .map((ws: any) => ({
            workstation_id: ws.workstation_id,
            workstation_name: ws.workstation_name,
            current_amount: ws.current_amount,
          }));
        setWorkstations(open_ws);
      })
      .catch((error: any) => {
        console.error('Error fetching workstations:', error);
        toast.error('Error al obtener las estaciones de trabajo.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, localId, userId]);

  // 2) Auto-select the first workstation when the list arrives
  useEffect(() => {
    if (workstations.length > 0 && selectedWorkstation === null) {
      setSelectedWorkstation(workstations[0].workstation_id);
    }
  }, [workstations, selectedWorkstation]);

  // 3) Update displayed stored amount whenever the selection changes
  useEffect(() => {
    if (selectedWorkstation !== null) {
      const ws = workstations.find(w => w.workstation_id === selectedWorkstation);
      setStoredAmount(ws ? ws.current_amount : 0);
    }
  }, [selectedWorkstation, workstations]);

  // 4) Propagate the payload every time the selected workstation changes
  useEffect(() => {
    const payload: any = { refundType: 1 };
    if (selectedWorkstation !== null) {
      payload.workstationId = selectedWorkstation;
    }
    setPayload(payload);
  }, [selectedWorkstation, setPayload]);

  useEffect(() => {
    // don't validate when the modal is hidden or still loading
    if (!show || isLoading) {
      setError(null)
      setIsDisabled(true) // Disable while loading
      return
    }

    if (storedAmount < amount) {
      setIsDisabled(true)
      setError("El monto en caja es insuficiente")
    } else {
      setIsDisabled(false)
      setError(null)
    }
  }, [show, isLoading, storedAmount, amount, setIsDisabled])

  // Don't render content while loading
  if (isLoading && show) {
    return (
      <Form className='receipt-cash-refund'>
        <div className="workstation-stored-amount">
          <div className='info'>
            <span>Cargando...</span>
          </div>
        </div>
      </Form>
    );
  }

  return (
    <Form className='receipt-cash-refund'>
      {workstations.length > 1 && 
      <Form.Group className="mp-formgroup vertical">
        <div className="workstations-list">
          {workstations.map(ws => (
            <div
              key={ws.workstation_id}
              className={`workstation-item${ws.workstation_id === selectedWorkstation ? ' selected' : ''}`}
              onClick={() => setSelectedWorkstation(ws.workstation_id)}
            >
              {ws.workstation_name}
            </div>
          ))}
        </div>
      </Form.Group>
      }

      {!isLoading && (
        <div className="workstation-stored-amount">
          <div className='info'>
            <span>Balance en caja:</span>
            <div className={`amount ${error !== null ? 'inactive' : undefined}`}>
            {error && <FaTriangleExclamation/>} {formatCurrency(storedAmount)}
            </div>
          </div>
          <p> {error && error}</p>
        </div>
      )}
      <div className="abonar-button-container">
      {error !== null && 
      <Button className='mp-button light' onClick={() => navigate('/App/CajasManagement')}> 
      <FaMoneyBillTransfer/> Abonar
      </Button>}
      </div>
    </Form>
  );
};

export default CashRefund;
